import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import { useAuth } from '../context/AuthContext';
import {
  useTheme, LIGHT_THEMES, DARK_THEMES, DEFAULT_SITE_THEME,
} from '../context/ThemeContext';

const API_BASE = (process.env.REACT_APP_API_BASE || 'https://backend.parthmerchant.com').replace(/\/$/, '');

const saveBtnSx = {
  borderRadius: '8px', height: '40px', textTransform: 'none', fontWeight: 600,
  fontFamily: 'var(--font-sans)', background: 'var(--green)', color: 'var(--btn-text)',
  px: 2.25, boxShadow: 'none',
  '&:hover': { background: 'var(--green)', opacity: 0.9, boxShadow: '0 0 0 3px var(--green-glow)' },
  '&.Mui-disabled': { background: 'var(--green)', color: 'var(--btn-text)', opacity: 0.5 },
};

function ThemeGrid({ themes, selected, onSelect }) {
  return (
    <div className="settings-theme-grid">
      {themes.map((t) => {
        const active = t.id === selected;
        return (
          <button
            key={t.id}
            type="button"
            className={`settings-theme-card ${active ? 'active' : ''}`}
            onClick={() => onSelect(t.id)}
            aria-pressed={active}
          >
            <span className="settings-theme-swatch" style={{ background: t.bg }}>
              {t.palette.map((c, i) => <span key={i} className="sw" style={{ background: c }} />)}
              {active && (
                <span className="settings-theme-check"><CheckRoundedIcon sx={{ fontSize: 13 }} /></span>
              )}
            </span>
            <span className="settings-theme-label">{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Edits the light/dark palettes the public site serves, plus which of the two a
 * first-time visitor lands on.
 *
 * Selecting a palette re-themes this console immediately, so the grid doubles as
 * a live preview of what visitors would get. That preview is local: `saved`
 * holds what the server actually has, `draft` holds the edit, and leaving the
 * tab with unsaved edits restores `saved` so the console never sits in a state
 * the site isn't in.
 */
export default function Appearance() {
  const { token } = useAuth();
  const { setSiteTheme, isDark, setMode } = useTheme();

  const [saved, setSaved] = useState(null);   // null until the first load lands
  const [draft, setDraft] = useState(DEFAULT_SITE_THEME);
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Read by the unmount cleanup, which must not re-run on every edit.
  const savedRef = useRef(null);

  const showToast = useCallback((msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }, []);

  const adopt = useCallback((theme) => {
    const next = { ...DEFAULT_SITE_THEME, ...(theme || {}) };
    setSaved(next);
    savedRef.current = next;
    setDraft(next);
  }, []);

  // Load the whole content document: the theme block seeds the editor, and the
  // rest is needed to PUT it back without blanking the page copy.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/landing/content`);
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setContent(data);
        adopt(data.theme);
      } catch {
        if (!cancelled) adopt(DEFAULT_SITE_THEME);
      }
    })();
    return () => { cancelled = true; };
  }, [adopt]);

  // Live-preview the draft in this console.
  useEffect(() => { setSiteTheme(draft); }, [draft, setSiteTheme]);

  // Restore the server's theme when navigating away with unsaved edits.
  useEffect(() => () => {
    if (savedRef.current) setSiteTheme(savedRef.current);
  }, [setSiteTheme]);

  const dirty = saved !== null && JSON.stringify(draft) !== JSON.stringify(saved);

  const handleSave = async () => {
    setSaving(true);
    try {
      // The API stores the page as one document, so read-modify-write: send the
      // copy we loaded back with only the theme block replaced. A PUT carrying
      // just the theme would blank the hero/about/globe copy.
      let base = content;
      if (!base) {
        const current = await fetch(`${API_BASE}/api/landing/content`);
        if (!current.ok) throw new Error(`Could not read current content (${current.status})`);
        base = await current.json();
      }

      const res = await fetch(`${API_BASE}/api/landing/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ...base, theme: draft }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `${res.status}` }));
        throw new Error(err.detail || `${res.status}`);
      }
      const result = await res.json();
      setContent(result);
      adopt(result.theme);
      showToast('Site theme saved', 'success');
    } catch (e) {
      showToast(e.message || 'Could not save the theme', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admset">
      {toast && <div className={`boards-toast boards-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="admset-banner sa-landing-banner">
        <div className="admset-banner-id">
          <span className="admset-eyebrow">Admin</span>
          <h1 className="admset-name">Appearance</h1>
          <p className="admset-email">
            Choose the light and dark palettes parthmerchant.com serves, and which one
            new visitors see first.
          </p>
        </div>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSave}
          disabled={saving || !dirty}
          sx={saveBtnSx}
        >
          {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
        </Button>
      </div>

      <div className="admset-grid">
        <section className="admset-card">
          <div className="admset-card-head">
            <LightModeRoundedIcon sx={{ fontSize: 18 }} />
            <h2>Light palette</h2>
          </div>
          <p className="admset-card-desc">
            What visitors get in light mode. Pick one to preview it here.
          </p>
          <ThemeGrid
            themes={LIGHT_THEMES}
            selected={draft.lightTheme}
            onSelect={(lightTheme) => { setDraft(d => ({ ...d, lightTheme })); setMode('light'); }}
          />
        </section>

        <section className="admset-card">
          <div className="admset-card-head">
            <DarkModeRoundedIcon sx={{ fontSize: 18 }} />
            <h2>Dark palette</h2>
          </div>
          <p className="admset-card-desc">
            What visitors get in dark mode. Pick one to preview it here.
          </p>
          <ThemeGrid
            themes={DARK_THEMES}
            selected={draft.darkTheme}
            onSelect={(darkTheme) => { setDraft(d => ({ ...d, darkTheme })); setMode('dark'); }}
          />
        </section>

        <section className="admset-card">
          <div className="admset-card-head">
            <h2>Default mode</h2>
          </div>
          <p className="admset-card-desc">
            Where a first-time visitor starts. Anyone can flip it themselves from the
            site header, and their choice is remembered on their device.
          </p>
          <div className="settings-theme-basic">
            {[
              { mode: 'light', label: 'Light', Icon: LightModeRoundedIcon },
              { mode: 'dark',  label: 'Dark',  Icon: DarkModeRoundedIcon },
            ].map(({ mode, label, Icon }) => {
              const active = draft.defaultMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  className={`settings-basic-card ${active ? 'active' : ''}`}
                  onClick={() => { setDraft(d => ({ ...d, defaultMode: mode })); setMode(mode); }}
                  aria-pressed={active}
                >
                  <Icon sx={{ fontSize: 22 }} />
                  <span>{label}</span>
                  {active && <CheckRoundedIcon className="settings-basic-check" sx={{ fontSize: 16 }} />}
                </button>
              );
            })}
          </div>
          <p className="admset-hint">
            This console is showing the <b>{isDark ? 'dark' : 'light'}</b> palette.
            {dirty ? ' Unsaved changes — hit Save to publish them.' : ''}
          </p>
        </section>
      </div>
    </div>
  );
}
